import { DOCUMENT } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { first, take } from 'rxjs/operators';
import { RouterStateService } from './core/routing/router-state.service';
import { AppSettingsService } from './core/services/app-settings.service';
import { AUTOMATION_TESTING_CONST } from './core/services/automation-test-constants';
import { BxLogger } from './core/services/bx-logger.service';
import { DialogParams, DialogService } from './core/services/dialog.service';
import { LaunchDarklyService } from './core/services/launchdarkly.service';
import { StoresService } from './core/services/stores/stores.service';
import { TenantService } from './core/services/tenant.service';
import { TermsConditionsService } from './core/services/terms-conditions/terms-conditions.service';
import { UserMfaActionType, UserService } from './core/services/user';
import { RepoAccountService } from './data/repositories/repo-account.service';
import { RepoAuthService } from './data/repositories/repo-auth.service';
import { RepoSettingsService } from './data/repositories/repo-settings.service';
import { SharedDataService } from './data/services/shared-data.service';
import { LayoutService } from './pages/layout/layout.service';
import { NotificationBannerService } from './pages/layout/notifications/notification.service';
import { Sidebar2ToggleService } from './pages/layout/sidebar.2/sidebar2-toggle.service';

@UntilDestroy()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit {
    // @ts-ignore TS7008
    config;
    // @ts-ignore TS7008
    scopeDestroyer1;
    // @ts-ignore TS2564
    layout: string;
    // @ts-ignore TS7008
    app;
    // @ts-ignore TS2564
    displayNotificationsPanel: boolean;

    LAYOUT_BARE = 'bare';
    LAYOUT_MAIN = 'main';

    constructor(
        public notificationBannerService: NotificationBannerService,
        public layoutService: LayoutService,
        public sidebar2Toggle: Sidebar2ToggleService,
        private appSettings: AppSettingsService,
        private storesService: StoresService,
        public sharedData: SharedDataService,
        public settings: RepoSettingsService,
        public lds: LaunchDarklyService,
        private cd: ChangeDetectorRef,
        private dialogService: DialogService,
        private termsAndConditionsService: TermsConditionsService,
        private readonly userService: UserService,
        private readonly repoAccountService: RepoAccountService,
        private renderer: Renderer2,
        @Inject(DOCUMENT) private _document: Document,
        private router: Router,
        private routerStateSerice: RouterStateService,
        private repoAuthService: RepoAuthService,
        private logger: BxLogger,
        private tenantService: TenantService
    ) {}

    ngOnInit() {
        this.injectBodyClasses();
        this.app = this.appSettings.app;
        this.setLayout();

        this.routerStateSerice.routeChanged$.pipe(untilDestroyed(this)).subscribe(() => {
            this.setLayout();
        });

        this.storesService.hasUserEnteredApp$.pipe(untilDestroyed(this)).subscribe(() => {
            if (this.sharedData.UserData.tenant?.DisplayPaymentIssue) {
                // Timeout is here for cosmetic reasons, it is not actually waiting on anything
                setTimeout(() => {
                    this.showSubscriptionErrorAlert();
                }, 1000);
            }
        });

        this.storesService.isStartUpComplete$.pipe(untilDestroyed(this)).subscribe((value) => {
            if (value) this.termsAndConditionsService.verifyTermsAndConditionsAccepted();
        });

        if (this.sharedData.UserData.tenant?.BillingCountry === 'United States') {
            this.loadOneBuild();
        }
    }

    ngAfterViewInit() {
        this.storesService.hideSplashScreen$.pipe(take(1)).subscribe(() => {
            document.body.classList.remove('splash');
            // @ts-ignore TS2531
            document.querySelector('#preloader').remove();
        });

        this.displayNotificationsPanel = this.showNotifications();
        this.cd.detectChanges();

        this.storesService.showMfaReminder$.pipe(untilDestroyed(this)).subscribe(async () => {
            const tncAccepted = await this.tenantService.getTermsAndConditionsAccepted();
            if (tncAccepted?.accepted) {
                this.#showMfaReminder();
            }
        });
    }

    setLayout() {
        const routeName = this.routerStateSerice.currentRouteName ?? '';
        if (routeName.includes('auth') || routeName.includes('onboarding') || routeName.includes('preview-proposal')) {
            this.layout = this.LAYOUT_BARE;
        } else if (routeName) {
            this.layout = this.LAYOUT_MAIN;
        }
    }

    injectBodyClasses() {
        if (this.appSettings.columnLayout !== 'double') {
            document.body.classList.remove('navigation-v2-double');
            document.body.classList.add('navigation-v2-single');
        } else {
            document.body.classList.remove('navigation-v2-single');
            document.body.classList.add('navigation-v2-double');
        }
        if (this.appSettings.loggedIn) {
            document.body.classList.add('logged-in');
        }
        if (this.layoutService.showSecondaryNav()) {
            document.body.classList.add('nav-v2-secondary');
        }
        if (this.layoutService.isMobile()) {
            document.body.classList.remove('is-not-mobile');
            document.body.classList.add('is-mobile');
        } else {
            document.body.classList.remove('is-mobile');
            document.body.classList.add('is-not-mobile');
        }
    }

    showMobileHeader() {
        if (this.layoutService.isMobile()) {
            const routeName = this.routerStateSerice.currentRouteName ?? '';
            if (
                routeName.includes('estimates.view') ||
                routeName.includes('leads.view') ||
                routeName.includes('jobs.view') ||
                routeName.includes('customers.view') ||
                routeName.includes('contacts.view') ||
                routeName.includes('pricing.pricelistview') ||
                routeName.includes('pricing2.view')
            ) {
                return false;
            } else return true;
        } else return false;
    }

    // BX-247 Estimate router top bar

    getContentWrapperClasses() {
        let classString = '';
        const navLayouts = this.layoutService.getNavV2Layouts();
        if (navLayouts) classString = `main-content--nav-v2-${navLayouts}`;

        /* Offset the top if there's a secondary nav bar */
        const secondaryNav = this.layoutService.showSecondaryNav();
        if (secondaryNav) classString += '  main-content--secondary-nav';

        /* Offset the top if we're showing notifications bar or the nav toggle */

        return classString;
    }

    // ***************************************
    // We use this with ng-include to determine which html to load for desktop/mobile views
    // Using hidden-xs or visible-xs, angular still loads the html and creates watchers even if elements are hidden
    // Instead, use this:
    // <div ng-if="isMobile()" ng-include="/path/mobile.html"></div>
    // <div ng-if="isDesktop()" ng-include="/path/desktop.html"></div>

    reload() {
        location.reload();
    }

    hideBrowserWarning() {
        this.sharedData.Common.ShowUpgradeWarning = false;
    }

    showGlobalHeader() {
        return !this.layoutService.isMobile() && this.lds.get('global-notifications');
    }
    showNotifications() {
        return !this.layoutService.isMobile() && !this.settings.NotificationsDismissed;
    }

    showSubscriptionErrorAlert() {
        let dialogText = '';

        if (history.state.inactive === 'true') return;

        if (this.sharedData.UserData.tenant?.SubscriptionPaymentStatus === 'not paid') {
            dialogText =
                'Your credit card may have expired, please update your details. Your account can be cancelled if action is not taken.';
        } else if (
            this.sharedData.UserData.tenant?.SubscriptionStatus === 'past_due' ||
            this.sharedData.UserData.tenant?.SubscriptionStatus === 'failed'
        ) {
            dialogText = 'Pending unpaid invoice/s have been found. Your account can be cancelled if action is not taken.';
        }
        const params: DialogParams = {
            title: 'An issue was found with your subscription',
            text: dialogText,
            type: 'warning',
            cancelText: 'Cancel',
            okText: 'Go to Subscription',
            customClass: {
                popup: 'bx-dialog bx-dialog-warning',
                confirmButton: 'dialog-subscription-confirm-btn',
                cancelButton: 'dialog-subscription-cancel-btn',
            },
        };
        this.dialogService.show(params).then(
            () => {
                this.router.navigate(['/account', 'subscription']);
            },
            () => ({})
        );
    }

    #showMfaReminder() {
        if (this.sharedData.UserData.tenant.ID_Reseller?.toUpperCase() === AUTOMATION_TESTING_CONST.resellerId) {
            return;
        }

        const forceMfaSetup = this.lds.get('remove-mfa-setup-cancel-button') ?? false;

        const title = forceMfaSetup ? 'Keep your Buildxact account safe' : 'Protect your data with 2-Step Verification';
        const message = forceMfaSetup
            ? `To better protect your Buildxact account, we're improving security by removing password-based logins. Going forward, you'll need to use a more secure authentication method. 
                    <br><br>
                    Set up one of the following options now to ensure uninterrupted access.
                    <br>
                    <b>One-Time Passcode (OTP)</b>: Remove your password and receive a login code via email.
                    <br>
                    <b>2-Step Verification</b>: Add an extra layer of security with an authenticator app.
                    <br><br>
                    Choose your preferred option below:`
            : `Prevent any unauthorized access to your Buildxact account by enabling 2-Step Verification. 
            This added layer of security means that when you log into Buildxact you will be asked to enter a verification code that has been sent to an app on your mobile device. `;

        const params: DialogParams = {
            title: title,
            text: message,
            type: 'warning',
            cancelText: forceMfaSetup ? 'Set Up OTP' : 'Later',
            showCancel: true,
            okText: forceMfaSetup ? 'Set Up 2-Step Verification' : 'Setup Now',
            allowHtml: forceMfaSetup,
            customClass: {
                popup: 'bx-dialog bx-dialog-warning',
                confirmButton: 'dialog-subscription-confirm-btn',
                cancelButton: 'dialog-subscription-cancel-btn',
            },
        };

        this.dialogService.show(params).then(
            () => {
                this.userService
                    .toggleMfa(UserMfaActionType.MfaReminder, true)
                    .pipe(take(1))
                    .subscribe(() => {
                        this.repoAccountService.logOut();
                    });
            },
            () => {
                if (!forceMfaSetup) {
                    this.userService.setUserMfaReminder();
                } else {
                    setTimeout(() => {
                        this.confirmRemovePasswordFromMfaSetup();
                    }, 500);
                }
            }
        );
    }

    confirmRemovePasswordFromMfaSetup() {
        const params: DialogParams = {
            type: 'info',
            title: 'Remove your password',
            text: 'If you remove your password, you will only be able to login via sending a code to your email address.',
            okText: 'OK',
            cancelText: 'Cancel',
        };

        this.dialogService.show(params).then(
            () => {
                this.repoAuthService
                    .removePassword()
                    .pipe(first())
                    .subscribe(() => {
                        this.sharedData.UserData.user.UserHasSetupPassword = false;
                        this.logger.success('Your password has been removed');
                    });
            },
            () => {
                setTimeout(() => {
                    this.#showMfaReminder();
                }, 500);
            }
        );
    }

    loadOneBuild() {
        const s = this.renderer.createElement('script');
        s.type = 'text/javascript';
        s.src = 'https://cdn.1build.com/widget.js';
        this.renderer.appendChild(this._document.body, s);
    }
}
